import { FloorPlanStatusEnum } from '@api/enum';
import { IFloorPlanModel } from '@api/models';
import { createFloorPlan, updateFloorPlan } from '@api/services/site-walk.service';
import { useMutation } from '@tanstack/react-query';

import { useMemo } from 'react';
import * as Yup from 'yup';

/* -------------------------------------------------------------------------------------------------
 * Hook: useRFIsProcoreShareForm
 * -----------------------------------------------------------------------------------------------*/

interface IUpdatefloorPlanOptions {
    clientId: string;
    projectId: string;
    floorPlan: IFloorPlanModel;
}

const useUpdateFloorPlanForm = (options: IUpdatefloorPlanOptions) => {
    const { clientId, projectId, floorPlan } = options;

    const schema = useMemo(() => {
        return Yup.object({
            name: Yup.string().required('This field is required'),
            folder: Yup.string().required('This field is required'),
            status: Yup.string().required('This field is required'),
            timezone: Yup.string().required('This field is required'),
        });
    }, []);

    const initialValues: Pick<
        IFloorPlanModel,
        'name' | 'folder' | 'location' | 'status' | 'timezone' | 'storage' | 'imageName'
    > = useMemo(() => {
        if (!floorPlan) {
            return {
                name: '',
                folder: '',
                location: {
                    latitude: '',
                    longitude: '',
                },
                timezone: '',
                storage: '',
                status: FloorPlanStatusEnum.DISABLED,
                imageName: '',
            };
        }
        return {
            name: floorPlan.name,
            folder: floorPlan.folder,
            location: {
                latitude: floorPlan?.location?.latitude,
                longitude: floorPlan?.location?.longitude,
            },

            timezone: floorPlan.timezone,
            storage: floorPlan.storage,
            status: floorPlan.status,
            imageName: floorPlan.imageName,
        };
    }, []);

    const updateFloorPlanMutation = useMutation(
        (
            payload: Pick<
                IFloorPlanModel,
                'name' | 'folder' | 'location' | 'status' | 'timezone' | 'storage' | 'imageName'
            >
        ) => updateFloorPlan(clientId, projectId, floorPlan._id, payload)
    );

    const submit = async (
        values: Pick<IFloorPlanModel, 'name' | 'folder' | 'location' | 'status' | 'timezone' | 'storage' | 'imageName'>
    ) => {
        try {
            await updateFloorPlanMutation.mutateAsync(values);
        } catch (err: any) {
            throw new Error(err);
        }
    };

    return {
        initialValues,
        schema,
        isSubmitting: updateFloorPlanMutation.isLoading,
        submit,
    };
};

export default useUpdateFloorPlanForm;
